import React from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix default marker icon paths for Leaflet when bundled by Webpack/Vite
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const TRACK_COLORS = {
  simple: '#2563eb',
  complete: '#16a34a',
};

const formatTimestamp = (value) => {
  if (!value) return 'No timestamp available';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return String(value);
  }
};

const sanitizePoints = (points = []) =>
  points.filter((point) =>
    point && typeof point.lat === 'number' && typeof point.lng === 'number'
  );

const DeviceRouteMap = ({ tracks, deviceName, activePoint }) => {
  const simpleEntries = sanitizePoints(tracks?.simple || []);
  const completeEntries = sanitizePoints(tracks?.complete || []);
  const combinedEntries = sanitizePoints(tracks?.combined || []);

  const routeEntries = combinedEntries.length
    ? combinedEntries
    : [...simpleEntries, ...completeEntries];

  const simpleCoords = simpleEntries.map((p) => [p.lat, p.lng]);
  const completeCoords = completeEntries.map((p) => [p.lat, p.lng]);
  const routeCoords = routeEntries.map((p) => [p.lat, p.lng]);

  if (routeCoords.length === 0) {
    return (
      <div className="p-4 border rounded-lg bg-muted/40">
        <p className="text-sm text-muted-foreground">
          No simple or complete position messages were found for this device in the selected period.
        </p>
      </div>
    );
  }

  const mapBounds =
    routeCoords.length > 1 ? L.latLngBounds(routeCoords) : null;
  const center = routeCoords[0];
  const startEntry = routeEntries[0];
  const endEntry = routeEntries[routeEntries.length - 1];

  const totalSimple = simpleEntries.length;
  const totalComplete = completeEntries.length;

  const mapKey = `${deviceName || 'device'}-${routeCoords.length}`;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-4 text-xs uppercase tracking-wide text-muted-foreground">
        <div className="flex items-center gap-2">
          <span
            className="inline-block w-3 h-3 rounded-full"
            style={{ backgroundColor: TRACK_COLORS.simple }}
          />
          Simple ({totalSimple})
        </div>
        <div className="flex items-center gap-2">
          <span
            className="inline-block w-3 h-3 rounded-full"
            style={{ backgroundColor: TRACK_COLORS.complete }}
          />
          Complete ({totalComplete})
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="font-semibold">Start:</span>
          {formatTimestamp(startEntry?.timestamp_iso || startEntry?.fix_time)}
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="font-semibold">End:</span>
          {formatTimestamp(endEntry?.timestamp_iso || endEntry?.fix_time)}
        </div>
      </div>

      <div className="h-80 rounded-xl overflow-hidden border border-border shadow-sm">
        <MapContainer
          key={mapKey}
          style={{ height: '100%', width: '100%' }}
          bounds={mapBounds || undefined}
          center={mapBounds ? undefined : center}
          zoom={mapBounds ? undefined : 11}
          scrollWheelZoom={false}
          className="rounded-xl"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {simpleCoords.length > 1 && (
            <Polyline positions={simpleCoords} pathOptions={{ color: TRACK_COLORS.simple, weight: 4, opacity: 0.8 }} />
          )}

          {completeCoords.length > 1 && (
            <Polyline positions={completeCoords} pathOptions={{ color: TRACK_COLORS.complete, weight: 4, opacity: 0.8 }} />
          )}

          {startEntry && (
            <Marker position={[startEntry.lat, startEntry.lng]}>
              <Popup>
                <div className="text-sm">
                  <p className="font-semibold">Route start</p>
                  <p className="text-xs text-muted-foreground">
                    {formatTimestamp(startEntry.timestamp_iso || startEntry.fix_time)}
                  </p>
                </div>
              </Popup>
            </Marker>
          )}

          {endEntry && routeEntries.length > 1 && (
            <Marker position={[endEntry.lat, endEntry.lng]}>
              <Popup>
                <div className="text-sm">
                  <p className="font-semibold">Route end</p>
                  <p className="text-xs text-muted-foreground">
                    {formatTimestamp(endEntry.timestamp_iso || endEntry.fix_time)}
                  </p>
                </div>
              </Popup>
            </Marker>
          )}

          {activePoint && typeof activePoint.lat === 'number' && typeof activePoint.lng === 'number' && (
            <CircleMarker
              center={[activePoint.lat, activePoint.lng]}
              radius={8}
              pathOptions={{ color: '#f97316', weight: 2, fillColor: '#fb923c', fillOpacity: 0.9 }}
            />
          )}
        </MapContainer>
      </div>
    </div>
  );
};

export default DeviceRouteMap;
